/**
 * Dost polish compare modal (Phase 4.5-C).
 *
 * Used from:
 *   - RSVP wizard "About Me" step
 *   - User profile "About Me" edit UI
 *
 * Auth: either a user JWT (via axios default) OR a guest edit_token passed
 * as the `editToken` prop. If both are absent, the modal shows a friendly
 * "sign in to use Dost" state and disables the polish button.
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Sparkles, RefreshCcw, Check, Pencil, RotateCcw, X } from 'lucide-react';
import api from '@/utils/api';
import { usePixels } from '@/contexts/PixelContext';

const TONES = [
  { value: 'warm', label: 'Warm' },
  { value: 'confident', label: 'Confident' },
  { value: 'casual', label: 'Casual' },
  { value: 'thoughtful', label: 'Thoughtful' },
  { value: 'playful', label: 'Playful' },
  { value: 'concise', label: 'Concise' },
];

const DostPolishModal = ({ open, onClose, originalText, editToken, uploadSessionToken, regId, onAccept }) => {
  const [tone, setTone] = useState('warm');
  const [phase, setPhase] = useState('tone'); // tone | loading | compare | error | quota
  const [polished, setPolished] = useState('');
  const [editedPolished, setEditedPolished] = useState('');
  const [editing, setEditing] = useState(false);
  const [meta, setMeta] = useState({ latency_ms: 0, tokens_used: 0 });
  const [quota, setQuota] = useState({ used_today: 0, limit: 5, remaining: 5 });
  const [errMsg, setErrMsg] = useState('');
  const { trackEvent } = usePixels();

  const authHeaders = () => {
    const h = {};
    if (editToken) h['X-Edit-Token'] = editToken;
    if (uploadSessionToken) h['X-Upload-Session'] = uploadSessionToken;
    return h;
  };

  const fetchQuota = async () => {
    try {
      const { data } = await api.get('/dost/quota?feature=polish_about_me', { headers: authHeaders() });
      setQuota(data);
    } catch (_) { /* non-fatal */ }
  };

  useEffect(() => {
    if (open) {
      setPhase('tone');
      setEditing(false);
      setPolished(''); setEditedPolished(''); setErrMsg('');
      fetchQuota();
    }
  }, [open]);

  const doPolish = async (isRegen = false) => {
    // Defensive: onClick handlers pass a SyntheticEvent when doPolish is
    // used directly as `onClick={doPolish}` instead of `onClick={() => doPolish()}`.
    // A SyntheticEvent leaks a `window` reference via target.ownerDocument,
    // so axios.JSON.stringify crashes with "Converting circular structure
    // to JSON". Coerce anything non-boolean to false.
    const regenerate = isRegen === true;
    setPhase('loading');
    setErrMsg('');
    let body;
    try {
      body = {
        text: typeof originalText === 'string' ? originalText : '',
        tone: typeof tone === 'string' ? tone : 'warm',
        regenerate,
      };
      if (typeof regId === 'string' && regId) body.reg_id = regId;
    } catch (buildErr) {
      console.error('[Dost polish] request build failed:', buildErr?.message || buildErr);
      setErrMsg('Something went wrong preparing your request. Refresh and try again.');
      setPhase('error');
      return;
    }
    try {
      const { data } = await api.post('/dost/polish-about-me', body, { headers: authHeaders() });
      setPolished(data.polished_text || '');
      setEditedPolished(data.polished_text || '');
      setMeta({ latency_ms: data.latency_ms || 0, tokens_used: data.tokens_used || 0 });
      setPhase('compare');
      trackEvent('Use Dosti Polish', {
        feature: 'polish_with_dost',
        surface: 'rsvp_or_profile_about_me',
      });
      // refresh quota
      fetchQuota();
    } catch (e) {
      const status = e?.response?.status;
      // Log the full failure so future debugging isn't a black box.
      // Rendered as a single line in the browser console.
      console.error('[Dost polish] failed', {
        status,
        detail: e?.response?.data?.detail,
        data: e?.response?.data,
        message: e?.message,
      });
      if (status === 429) {
        setErrMsg(e?.response?.data?.detail || 'You\'ve used today\'s polishes. Try again tomorrow.');
        setPhase('quota');
      } else {
        setErrMsg(e?.response?.data?.detail || e?.message || 'Dost is having a moment. Try again shortly.');
        setPhase('error');
      }
    }
  };

  const accept = () => {
    const finalText = editing ? editedPolished : polished;
    onAccept?.(finalText);
    onClose?.();
  };

  const isEmpty = !(originalText || '').trim();

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-800 flex items-center justify-center">
              <Sparkles className="h-4 w-4" />
            </div>
            <DialogTitle>Polish with Dost</DialogTitle>
          </div>
          <p className="text-xs text-neutral-500 mt-1">
            {quota.remaining} of {quota.limit} polishes left today.
            {' '}Dost preserves your meaning while improving the tone, clarity, and flow.
          </p>
        </DialogHeader>

        {phase === 'tone' && (
          <div className="space-y-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-secondary-700 mb-1">Your text</div>
              <div className="p-3 rounded-lg bg-neutral-50 border border-neutral-100 text-sm text-neutral-700 whitespace-pre-wrap min-h-[80px] max-h-[220px] overflow-auto" data-testid="polish-original">
                {originalText || <span className="text-neutral-400 italic">Write something first, then come back.</span>}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-secondary-700 mb-1">Tone</div>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger data-testid="polish-tone-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TONES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button
                onClick={() => doPolish(false)}
                disabled={isEmpty || quota.remaining <= 0}
                className="bg-primary-800 hover:bg-primary-900 text-white"
                data-testid="polish-run-btn"
              >
                <Sparkles className="h-4 w-4 mr-1.5" />
                Polish with Dost
              </Button>
            </DialogFooter>
          </div>
        )}

        {phase === 'loading' && (
          <div className="py-12 flex flex-col items-center gap-3" data-testid="polish-loading">
            <div className="w-10 h-10 rounded-full bg-secondary-100 text-secondary-800 flex items-center justify-center animate-pulse">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="text-sm text-primary-900 font-medium">Dost is polishing your bio...</div>
            <div className="text-xs text-neutral-500">Usually takes 5 to 15 seconds.</div>
          </div>
        )}

        {phase === 'compare' && (
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Original</div>
                <div className="p-3 rounded-lg bg-neutral-50 border border-neutral-100 text-sm text-neutral-700 whitespace-pre-wrap min-h-[160px] max-h-[240px] overflow-auto">
                  {originalText}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[10px] uppercase tracking-widest text-secondary-700">Polished by Dost</div>
                  {!editing ? (
                    <button onClick={() => setEditing(true)} className="text-xs text-primary-800 hover:text-primary-900 flex items-center gap-1" data-testid="polish-edit-btn">
                      <Pencil className="h-3 w-3" /> Edit
                    </button>
                  ) : (
                    <button onClick={() => setEditing(false)} className="text-xs text-primary-800 hover:text-primary-900 flex items-center gap-1">
                      Done
                    </button>
                  )}
                </div>
                {editing ? (
                  <Textarea
                    value={editedPolished}
                    onChange={(e) => setEditedPolished(e.target.value)}
                    rows={8}
                    className="text-sm"
                    data-testid="polish-edit-area"
                  />
                ) : (
                  <div className="p-3 rounded-lg bg-secondary-50/40 border border-secondary-200 text-sm text-primary-900 whitespace-pre-wrap min-h-[160px] max-h-[240px] overflow-auto" data-testid="polish-result">
                    {polished || <span className="text-neutral-400 italic">No output</span>}
                  </div>
                )}
              </div>
            </div>

            <div className="text-[11px] text-neutral-500 flex items-center gap-2">
              <Badge variant="outline" className="text-[10px]">tone: {tone}</Badge>
              <span>{meta.latency_ms}ms</span>
              <span>·</span>
              <span>~{meta.tokens_used} tokens</span>
            </div>

            <DialogFooter className="flex-wrap gap-2">
              <Button variant="outline" onClick={onClose} data-testid="polish-restore-btn">
                <X className="h-4 w-4 mr-1" /> Restore original
              </Button>
              <Button variant="outline" onClick={() => setPhase('tone')} data-testid="polish-try-tone-btn">
                <RotateCcw className="h-4 w-4 mr-1" /> Try different tone
              </Button>
              <Button variant="outline" onClick={() => doPolish(true)} disabled={quota.remaining <= 0} data-testid="polish-regenerate-btn">
                <RefreshCcw className="h-4 w-4 mr-1" /> Regenerate
              </Button>
              <Button
                onClick={accept}
                className="bg-primary-800 hover:bg-primary-900 text-white"
                data-testid="polish-accept-btn"
              >
                <Check className="h-4 w-4 mr-1" /> Accept
              </Button>
            </DialogFooter>
          </div>
        )}

        {(phase === 'error' || phase === 'quota') && (
          <div className="py-8 flex flex-col items-center gap-3" data-testid="polish-error">
            <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-neutral-400" />
            </div>
            <div className="text-sm text-primary-900 font-medium text-center max-w-md">
              {errMsg}
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={onClose}>Close</Button>
              {phase === 'error' && (
                <Button onClick={() => doPolish(false)} className="bg-primary-800 hover:bg-primary-900 text-white">Try again</Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DostPolishModal;
